import {ShapeUtils, Vector2} from 'three';
import type {
    SketchRegionBuildContext,
    SketchRegionFaceLoop,
    SketchRegionPolyline,
    SketchRegionSourceSegment,
    SketchRegionVec2,
    SketchRegionVec3,
} from './SketchRegionTypes';
import {
    hashString,
    liftPointFromPlane,
    normalizeLoopPoints3,
    pointInPolygon2,
    polygonCentroid2,
    projectPointToPlane,
} from './SketchRegionMath';
import {sampleSketchRegionEntity} from './SketchRegionPolylineSampler';
import {splitSketchRegionSegments} from './SketchRegionSegmentSplitter';
import {traceSketchRegionFaces} from './SketchRegionFaceTracer';
import {resolveSketchRegionLoopCurveSegments} from './SketchRegionCurveSegmentResolver';

/**
 * 对字符串数组去重排序。
 * @param values 原始数组。
 * @returns 去重排序后的数组。
 */
function uniqueSorted(values: string[]): string[] {
    return Array.from(new Set(values.filter((value) => value.length > 0))).sort();
}

/**
 * 比较两个字符串列表，保证多洞语义序列稳定。
 * @param left 左侧列表。
 * @param right 右侧列表。
 * @returns 排序结果。
 */
function compareStringLists(left: string[], right: string[]): number {
    const maxLength = Math.max(left.length, right.length);
    for (let index = 0; index < maxLength; index += 1) {
        const leftValue = left[index];
        const rightValue = right[index];
        if (leftValue === undefined) {
            return -1;
        }
        if (rightValue === undefined) {
            return 1;
        }
        if (leftValue !== rightValue) {
            return leftValue.localeCompare(rightValue);
        }
    }
    return 0;
}

/**
 * 区域输出构建来源。
 */
interface SketchRegionPayloadSource {
    /** 外环面环。 */
    loop: SketchRegionFaceLoop;
    /** 直接洞面环。 */
    holes: SketchRegionFaceLoop[];
    /** 已完成带洞三角剖分的网格。 */
    mesh: { positions: number[]; indices: number[] };
    /** 区域身份来源。 */
    provenance: RuntimeRegionProvenance;
}

/**
 * 将输入实体采样成折线。
 * @param entities 草图实体。
 * @param plane 草图平面。
 * @returns 折线集合。
 */
function samplePolylines(
    entities: SketchRegionEntityPayload[],
    plane: FindSketchRegionsPayload['plane'],
): SketchRegionPolyline[] {
    return entities
        .map((entity) => sampleSketchRegionEntity(entity, plane))
        .filter((polyline): polyline is SketchRegionPolyline => polyline !== null);
}

/**
 * 从折线构建线段。
 * @param context 区域构建上下文。
 * @returns 原始线段集合。
 */
function buildSourceSegments(context: SketchRegionBuildContext): SketchRegionSourceSegment[] {
    return context.polylines.flatMap((polyline) => {
        const points = polyline.closed ? normalizeLoopPoints3(polyline.points) : polyline.points;
        const segmentCount = polyline.closed ? points.length : points.length - 1;
        return Array.from({length: Math.max(0, segmentCount)}, (_, index) => {
            const start3 = points[index]!;
            const end3 = points[(index + 1) % points.length]!;
            return {
                start: projectPointToPlane(start3, context.plane),
                end: projectPointToPlane(end3, context.plane),
                start3,
                end3,
                entityId: polyline.entityId,
                entityType: polyline.entityType,
                closedEntity: polyline.closed,
            };
        });
    });
}

/**
 * 解析实体闭合状态索引。
 * @param polylines 折线集合。
 * @returns 闭合状态表。
 */
function buildClosedEntityMap(polylines: SketchRegionPolyline[]): Map<string, boolean> {
    return new Map(polylines.map((polyline) => [polyline.entityId, polyline.closed]));
}

/**
 * 判断两个面环是否共享追踪顶点。
 * @param left 左面环。
 * @param right 右面环。
 * @returns 是否共享顶点。
 */
function loopsShareVertex(left: SketchRegionFaceLoop, right: SketchRegionFaceLoop): boolean {
    const leftKeys = new Set(left.vertexKeys);
    return right.vertexKeys.some((key) => leftKeys.has(key));
}

/**
 * 判断子面环是否严格位于父面环内部。
 * @param child 待分类子面环。
 * @param parent 候选父面环。
 * @returns 是否为严格包含。
 */
function isLoopStrictlyInsideLoop(
    child: SketchRegionFaceLoop,
    parent: SketchRegionFaceLoop,
): boolean {
    if (child === parent || Math.abs(child.area) >= Math.abs(parent.area) || loopsShareVertex(child, parent)) {
        return false;
    }

    /**
     * 使用全部子环顶点判断严格包含，避免凹形子轮廓的质心落到轮廓外时误判；
     * 共享顶点的分割线子区域会在上面的共享顶点检查中提前排除。
     */
    return child.points.every((point) => pointInPolygon2(point, parent.points));
}

/**
 * 为每个面环解析直接子面环。直接子面环表示该区域的洞；
 * 共享边界的开放线段拆分区域不会被归为洞。
 * @param loops 面环集合。
 * @returns 面环及其直接洞面环。
 */
function resolveLoopHoleRelations(loops: SketchRegionFaceLoop[]): Array<{
    loop: SketchRegionFaceLoop;
    holes: SketchRegionFaceLoop[];
}> {
    const parentIndexes = loops.map((loop, loopIndex) => {
        const parentCandidates = loops
            .map((candidate, candidateIndex) => ({candidate, candidateIndex}))
            .filter(({candidate}) => isLoopStrictlyInsideLoop(loop, candidate))
            .sort((left, right) => Math.abs(left.candidate.area) - Math.abs(right.candidate.area));
        return parentCandidates[0]?.candidateIndex ?? null;
    });

    return loops.map((loop, loopIndex) => ({
        loop,
        holes: loops.filter((_, childIndex) => parentIndexes[childIndex] === loopIndex),
    }));
}

/**
 * 解析区域内部实体。
 * @param center 区域中心二维坐标。
 * @param polylines 折线集合。
 * @param plane 草图平面。
 * @returns inside/outside 成员关系。
 */
function resolveMembership(
    center: SketchRegionVec2,
    polylines: SketchRegionPolyline[],
    plane: FindSketchRegionsPayload['plane'],
): { insideEntityIds: string[]; outsideEntityIds: string[] } {
    const insideEntityIds: string[] = [];
    const outsideEntityIds: string[] = [];
    polylines.forEach((polyline) => {
        if (!polyline.closed || polyline.points.length < 3) {
            return;
        }
        const polygon = normalizeLoopPoints3(polyline.points).map((point) => projectPointToPlane(point, plane));
        if (pointInPolygon2(center, polygon)) {
            insideEntityIds.push(polyline.entityId);
        } else {
            outsideEntityIds.push(polyline.entityId);
        }
    });
    return {
        insideEntityIds: uniqueSorted(insideEntityIds),
        outsideEntityIds: uniqueSorted(outsideEntityIds),
    };
}

/**
 * 构建区域来源。
 * @param loop 面环。
 * @param holeLoops 洞面环。
 * @param membershipProbe 区域内部探针点。
 * @param polylines 折线集合。
 * @param plane 草图平面。
 * @returns 来源信息。
 */
function buildProvenance(
    loop: SketchRegionFaceLoop,
    holeLoops: SketchRegionFaceLoop[],
    membershipProbe: SketchRegionVec2,
    polylines: SketchRegionPolyline[],
    plane: FindSketchRegionsPayload['plane'],
    stableIdByEntityId: ReadonlyMap<string, string>,
): RuntimeRegionProvenance {
    const closedEntityMap = buildClosedEntityMap(polylines);
    const membership = resolveMembership(membershipProbe, polylines, plane);
    const boundaryEntityIds = uniqueSorted(loop.boundaryEntityIds);
    const outerBoundaryEntityIds = uniqueSorted(boundaryEntityIds.filter((entityId) => closedEntityMap.get(entityId)));
    const holeBoundaryEntityIds = holeLoops
        .map((holeLoop) => uniqueSorted(
            holeLoop.boundaryEntityIds.filter((entityId) => closedEntityMap.get(entityId)),
        ))
        .filter((entityIds) => entityIds.length > 0)
        .sort(compareStringLists);
    const splitterEntityIds = uniqueSorted([
        ...loop.splitterEntityIds,
        ...holeLoops.flatMap((holeLoop) => holeLoop.splitterEntityIds),
        ...boundaryEntityIds.filter((entityId) => !closedEntityMap.get(entityId)),
        ...holeLoops.flatMap((holeLoop) => (
            holeLoop.boundaryEntityIds.filter((entityId) => !closedEntityMap.get(entityId))
        )),
    ]);
    /**
     * 语义键哈希必须使用跨会话稳定 ID：
     * 1. 运行时 uuid 在草图从离线包还原后必然变化，直接哈希会让保存的区域键永远失配，
     *    导致特征无法找回创建时选中的区域（碎裂成局部范围/重建失败）；
     * 2. 因此哈希输入优先映射为实体持久 ID（映射后重新排序保证确定性），
     *    旧数据缺失持久 ID 时回退运行时 ID 保持兼容；
     * 3. provenance 中存储的集合仍保留运行时 ID，供会话内选择联动等既有消费方使用。
     */
    const toStableSorted = (entityIds: readonly string[]): string[] => (
        [...entityIds].map((entityId) => stableIdByEntityId.get(entityId) ?? entityId).sort()
    );
    const stableHoles = holeBoundaryEntityIds.map((hole) => toStableSorted(hole)).sort(compareStringLists);
    /**
     * 语义键只哈希拓扑成员（边界/洞/分割实体的持久 ID）：
     * 探针式 inside/outside membership 会随坐标编辑变化（探针点移动改变点在多边形内外
     * 的判定），把它纳入哈希曾导致“编辑被吸收草图后语义键漂移 → 依赖特征找不回区域”。
     * 拓扑成员集合不随坐标编辑变化，才是区域跨会话身份的正确锚点。
     */
    const semanticKey = `sk_${hashString(JSON.stringify({
        boundary: toStableSorted(outerBoundaryEntityIds),
        holes: stableHoles,
        splitters: toStableSorted(splitterEntityIds),
    }))}`;
    /** 旧版语义键（含 membership 的历史算法），仅供旧文档绑定回退匹配，不进入新保存。 */
    const legacySemanticKey = `sk_${hashString(JSON.stringify({
        inside: toStableSorted(membership.insideEntityIds),
        outside: toStableSorted(membership.outsideEntityIds),
        boundary: toStableSorted(outerBoundaryEntityIds),
        holes: stableHoles,
        splitters: toStableSorted(splitterEntityIds),
    }))}`;
    return {
        semanticKey,
        legacySemanticKey,
        membership,
        boundary: {
            outerBoundaryEntityIds,
            holeBoundaryEntityIds,
            splitterEntityIds,
        },
    };
}

/**
 * 将运行时来源转成持久化身份。
 * @param provenance 运行时来源。
 * @returns 持久化身份。
 */
function buildIdentity(provenance: RuntimeRegionProvenance): PersistedRegionIdentity {
    const persistedProvenance: PersistedRegionProvenance = {
        membership: {
            insideEntityIds: [...provenance.membership.insideEntityIds],
            outsideEntityIds: [...provenance.membership.outsideEntityIds],
        },
        boundary: {
            outerBoundaryEntityIds: [...provenance.boundary.outerBoundaryEntityIds],
            holeBoundaryEntityIds: provenance.boundary.holeBoundaryEntityIds.map((hole) => [...hole]),
            splitterEntityIds: [...provenance.boundary.splitterEntityIds],
        },
    };
    return {
        semanticKey: provenance.semanticKey,
        locatorKey: provenance.locatorKey,
        provenance: persistedProvenance,
    };
}

/**
 * 将二维外环三角剖分为网格。
 * @param outer2d 二维外环。
 * @param outer3d 三维外环。
 * @param holes2d 二维洞环。
 * @param holes3d 三维洞环。
 * @returns 网格点与索引。
 */
function triangulateLoop(
    outer2d: SketchRegionVec2[],
    outer3d: SketchRegionVec3[],
    holes2d: SketchRegionVec2[][],
    holes3d: SketchRegionVec3[][],
): { positions: number[]; indices: number[] } {
    const triangles = ShapeUtils.triangulateShape(
        outer2d.map((point) => new Vector2(point[0], point[1])),
        holes2d.map((hole) => hole.map((point) => new Vector2(point[0], point[1]))),
    );
    return {
        positions: [
            ...outer3d,
            ...holes3d.flatMap((hole) => hole),
        ].flatMap((point) => point),
        indices: triangles.flatMap((triangle) => triangle),
    };
}

/**
 * 从三角网格中读取二维点。
 * @param positions 三维点扁平数组。
 * @param vertexIndex 顶点索引。
 * @param plane 草图平面。
 * @returns 二维点。
 */
function readMeshPoint2(
    positions: number[],
    vertexIndex: number,
    plane: FindSketchRegionsPayload['plane'],
): SketchRegionVec2 | null {
    const offset = vertexIndex * 3;
    if (offset < 0 || offset + 2 >= positions.length) {
        return null;
    }
    return projectPointToPlane([
        positions[offset]!,
        positions[offset + 1]!,
        positions[offset + 2]!,
    ], plane);
}

/**
 * 解析区域内部成员关系探针点。
 *
 * 使用最大三角面的质心，而不是外环质心，是为了处理环形区域：
 * 外环质心可能落在洞内，导致区域身份错误地把洞内实体识别为 inside。
 *
 * @param mesh 区域三角网格。
 * @param fallbackCenter 退化时的回退中心。
 * @param plane 草图平面。
 * @returns 位于区域内部的二维探针点。
 */
function resolveMembershipProbe(
    mesh: { positions: number[]; indices: number[] },
    fallbackCenter: SketchRegionVec2,
    plane: FindSketchRegionsPayload['plane'],
): SketchRegionVec2 {
    let bestArea = 0;
    let bestCentroid: SketchRegionVec2 | null = null;
    for (let index = 0; index + 2 < mesh.indices.length; index += 3) {
        const first = readMeshPoint2(mesh.positions, mesh.indices[index]!, plane);
        const second = readMeshPoint2(mesh.positions, mesh.indices[index + 1]!, plane);
        const third = readMeshPoint2(mesh.positions, mesh.indices[index + 2]!, plane);
        if (!first || !second || !third) {
            continue;
        }
        const area = Math.abs((first[0] * (second[1] - third[1])
            + second[0] * (third[1] - first[1])
            + third[0] * (first[1] - second[1])) * 0.5);
        if (area <= bestArea) {
            continue;
        }
        bestArea = area;
        bestCentroid = [
            (first[0] + second[0] + third[0]) / 3,
            (first[1] + second[1] + third[1]) / 3,
        ];
    }
    return bestCentroid ?? fallbackCenter;
}

/**
 * 为同语义区域补充定位键。
 * @param regions 区域集合。
 * @returns 已补充定位键的区域。
 */
function assignLocatorKeys(regions: SketchRegionPayloadSource[]): SketchRegionPayloadSource[] {
    const groups = new Map<string, SketchRegionPayloadSource[]>();
    regions.forEach((region) => {
        const group = groups.get(region.provenance.semanticKey) ?? [];
        group.push(region);
        groups.set(region.provenance.semanticKey, group);
    });

    groups.forEach((group) => {
        if (group.length <= 1) {
            return;
        }
        group
            .sort((left, right) => {
                const leftCenter = polygonCentroid2(left.loop.points);
                const rightCenter = polygonCentroid2(right.loop.points);
                return leftCenter[0] === rightCenter[0]
                    ? leftCenter[1] - rightCenter[1]
                    : leftCenter[0] - rightCenter[0];
            })
            .forEach((region, index) => {
                region.provenance.locatorKey = `loc_${index + 1}`;
            });
    });

    return regions;
}

/**
 * 构建区域输出。
 * @param context 构建上下文。
 * @param loops 面环集合。
 * @returns 区域输出。
 */
function buildRegionPayloads(
    context: SketchRegionBuildContext,
    loops: SketchRegionFaceLoop[],
): SketchRegionPayload[] {
    const loopRelations = resolveLoopHoleRelations(loops);
    //------ 区域语义键跨会话稳定 开始------
    // 实体运行时 ID → 持久 ID 映射；语义键哈希经此映射后不再受“离线包还原产生新 uuid”影响。
    const stableIdByEntityId = new Map<string, string>();
    context.entities.forEach((entity) => {
        const persistentId = (entity as {persistentId?: string}).persistentId;
        if (entity.entityId && typeof persistentId === 'string' && persistentId.length > 0) {
            stableIdByEntityId.set(entity.entityId, persistentId);
        }
    });
    //------ 区域语义键跨会话稳定 结束------
    const regionSources = assignLocatorKeys(loopRelations.map(({loop, holes}) => {
        const center2d = polygonCentroid2(loop.points);
        const mesh = triangulateLoop(
            loop.points,
            loop.points3,
            holes.map((hole) => hole.points),
            holes.map((hole) => hole.points3),
        );
        const membershipProbe = resolveMembershipProbe(mesh, center2d, context.plane);
        return {
            loop,
            holes,
            mesh,
            provenance: buildProvenance(loop, holes, membershipProbe, context.polylines, context.plane, stableIdByEntityId),
        };
    }));

    return regionSources.map(({loop, holes, mesh, provenance}) => {
        const center2d = polygonCentroid2(loop.points);
        const center = liftPointFromPlane(center2d, context.plane);
        const contourKey = [
            provenance.semanticKey,
            provenance.locatorKey ?? 'single',
        ].join(':');
        const identity = buildIdentity(provenance);
        const outerSegments = resolveSketchRegionLoopCurveSegments(
            loop,
            context.entities,
            context.plane,
        );
        const holeSegments = holes.map((hole) => resolveSketchRegionLoopCurveSegments(
            hole,
            context.entities,
            context.plane,
        ));
        return {
            sketchId: context.sketchId,
            id: `${context.sketchId}-region-${contourKey}`,
            contourKey,
            identity,
            provenance,
            positions: mesh.positions,
            indices: mesh.indices,
            normal: [...context.plane.normal] as SketchRegionVec3,
            center,
            xAxis: [...context.plane.xAxis] as SketchRegionVec3,
            yAxis: [...context.plane.yAxis] as SketchRegionVec3,
            outer: loop.points3,
            outerSegments: outerSegments.length > 0 ? outerSegments : undefined,
            holes: holes.map((hole) => hole.points3),
            holeSegments: holeSegments.some((segments) => segments.length > 0) ? holeSegments : undefined,
        };
    });
}

/**
 * 解析草图闭合区域。
 * @param payload Worker 区域解析载荷。
 * @returns 区域解析响应。
 */
export function findSketchRegions(payload: FindSketchRegionsPayload): FindSketchRegionsResponse {
    const context: SketchRegionBuildContext = {
        sketchId: payload.sketchId,
        plane: payload.plane,
        entities: payload.entities,
        polylines: samplePolylines(payload.entities, payload.plane),
    };
    const sourceSegments = buildSourceSegments(context);
    if (sourceSegments.length <= 0) {
        return {regions: []};
    }
    const graphEdges = splitSketchRegionSegments(sourceSegments);
    const loops = traceSketchRegionFaces(graphEdges);
    return {
        regions: buildRegionPayloads(context, loops),
    };
}

/**
 * 兼容旧分支调用习惯的区域解析入口。
 * @param entities 草图实体。
 * @param plane 草图平面。
 * @param sketchId 草图 ID。
 * @returns 区域列表。
 */
export function resolveSketchRegions(
    entities: SketchRegionEntityPayload[],
    plane: FindSketchRegionsPayload['plane'],
    sketchId = 'runtime-sketch',
): SketchRegionPayload[] {
    return findSketchRegions({sketchId, entities, plane}).regions;
}
